'use client';

import { useState, useEffect } from 'react';

interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
}

interface InteractiveChecklistProps {
  id: string; // Unique identifier for this checklist
  items: string[];
}

export default function InteractiveChecklist({ id, items }: InteractiveChecklistProps) {
  const [checklist, setChecklist] = useState<ChecklistItem[]>(() => {
    if (typeof window === 'undefined') {
      return items.map((text, index) => ({
        id: `${id}_${index}`,
        text,
        checked: false
      }));
    }
    try {
      const saved = localStorage.getItem(`checklist_${id}`);
      if (saved) return JSON.parse(saved);
    } catch {
      // Fall through to default
    }
    return items.map((text, index) => ({
      id: `${id}_${index}`,
      text,
      checked: false
    }));
  });

  const toggleItem = (itemId: string) => {
    const updated = checklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setChecklist(updated);
    localStorage.setItem(`checklist_${id}`, JSON.stringify(updated));
  };

  const resetChecklist = () => {
    const reset = checklist.map(item => ({ ...item, checked: false }));
    setChecklist(reset);
    localStorage.setItem(`checklist_${id}`, JSON.stringify(reset));
  };

  const progress = checklist.length > 0
    ? Math.round((checklist.filter(item => item.checked).length / checklist.length) * 100)
    : 0;

  if (checklist.length === 0) {
    return null;
  }

  return (
    <div className="my-6 p-6 bg-surface/50 rounded-lg border border-border-muted">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-foreground">Checklist</h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-foreground-tertiary">
            {progress}% complete ({checklist.filter(i => i.checked).length}/{checklist.length})
          </div>
          {progress > 0 && (
            <button
              onClick={resetChecklist}
              className="text-xs px-3 py-1 bg-accent-muted hover:bg-accent-muted text-accent rounded transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-surface rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-accent to-accent/70 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Checklist items */}
      <ul className="space-y-2">
        {checklist.map((item) => (
          <li key={item.id}>
            <label className="flex items-start gap-3 p-3 rounded hover:bg-surface/30 cursor-pointer transition-colors group">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() => toggleItem(item.id)}
                className="mt-0.5 w-5 h-5 rounded border-2 border-border text-accent focus:ring-2 focus:ring-ring/50 cursor-pointer"
              />
              <span className={`flex-1 ${item.checked ? 'text-foreground-tertiary line-through' : 'text-foreground/90'} transition-colors`}>
                {item.text}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {progress === 100 && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded text-green-400 text-sm text-center">
          🎉 Checklist complete! Great work!
        </div>
      )}
    </div>
  );
}
